import { useEffect, useMemo, useState } from 'react';

import { UserSettingsObjectType } from '~/actions/couples/types/userSettingsActionTypes';
import { fetchUserSettings } from '~/actions/couples/userSettingsActions';
import { useUserContext } from '~/contexts/UserContext';
import { useAppDispatch, useAppSelector } from '~/reducers';
import { getUserSettings } from '~/selectors/userSettingsSelectors';
import { UserSettingView } from '~/types/responseTypes';

type UseUserSettingsResult = {
  settings: Record<string, UserSettingView>;
  loaded: boolean;
};

/**
 * Fetches a list of user settings from the API and puts the results in redux.
 * Returns either the redux version of the key, or the API version of the key.
 *
 * The reason for returning either the redux version or the API version is so
 * we immediately have the result (API has responded, but redux hasn't updated
 * yet) and then to be stable after redux updates, or if the key is updated
 * (using a redux action) the most current value is returned.
 *
 * @param keys
 * @returns
 */
export const useUserSettings = (keys: string[] = []): UseUserSettingsResult => {
  const [loaded, setLoaded] = useState(false);
  const userContext = useUserContext();

  const selector = useMemo(() => {
    return getUserSettings(keys);
  }, [keys]);

  const userSettingFromRedux = useAppSelector(selector);
  const [userSettingFromApi, setUserSettingFromApi] = useState<UserSettingsObjectType>();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (userContext && !userContext.is_guest && keys.length > 0) {
      const fetchData = async () => {
        await dispatch(fetchUserSettings(keys))
          .then((response) => {
            if (response) {
              setUserSettingFromApi(response);
            }
          })
          .finally(() => setLoaded(true));
      };

      fetchData().catch(() => null);
    }

    if (userContext && userContext.is_guest) {
      setLoaded(true);
    }
    if (keys.length === 0) {
      setLoaded(true);
    }
  }, [dispatch, keys, userContext]);

  // We should always get back a UserSettingView from the API, even if the
  // flag hasn't been set before.  So this is a record of UserSettingView not
  // UserSettingView | undefined.
  const mergedSettings: Record<string, UserSettingView> = useMemo(
    () =>
      keys.reduce((result, current) => {
        return {
          ...result,
          [current]: userSettingFromRedux[current] || userSettingFromApi?.[current],
        };
      }, {}),
    [keys, userSettingFromApi, userSettingFromRedux]
  );

  return {
    settings: mergedSettings,
    loaded,
  };
};
export default useUserSettings;
